import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { ComboboxSearchField } from '@/components/common/ComboboxSearchField';
import { LOADING_PLACEHOLDER } from '@/constants/app.constants';
import { useDebounce } from '@/hooks/useDebounce';
import {
  COMBOBOX_LIST_CLASS,
  COMBOBOX_PANEL_CLASS,
  DROPDOWN_ITEM_CLASS,
} from '@/styles/floatingPanel';
import { isSameComboboxOption } from '@/utils/comboboxOption';
import { memberInstitutesQueryOptions } from '../api/question.api';
import type { MemberInstitute } from '../types/question.types';

interface MemberInstituteSelectProps {
  id: string;
  userId: number | undefined;
  value: number | undefined;
  onChange: (instituteId: number | undefined) => void;
}

interface InstituteOption {
  label: string;
  value: number;
  code: string;
  where: string;
}

export function MemberInstituteSelect({ id, userId, value, onChange }: MemberInstituteSelectProps) {
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 300);

  const { data, isLoading, isFetching } = useQuery(memberInstitutesQueryOptions(userId, search));

  const items: InstituteOption[] = (data ?? []).map((institute: MemberInstitute) => ({
    label: institute.instituteName ?? `#${institute.instituteCode ?? ''}`,
    value: institute.instituteId,
    code: institute.instituteCode === null ? '' : String(institute.instituteCode),
    where: [institute.thanaName, institute.districtName].filter(Boolean).join(', '),
  }));

  const selected = items.some((item) => item.value === value) ? (value ?? null) : null;

  return (
    <Combobox<number>
      items={items}
      value={selected}
      onValueChange={(next) => onChange(next ?? undefined)}
      isItemEqualToValue={isSameComboboxOption}
      filter={() => true}
      disabled={userId === undefined}
    >
      <ComboboxTrigger id={id}>
        <ComboboxValue placeholder={isLoading ? LOADING_PLACEHOLDER : 'Search by name or code'} />
        <ComboboxIcon />
      </ComboboxTrigger>

      <ComboboxContent className={COMBOBOX_PANEL_CLASS}>
        <ComboboxSearchField
          placeholder="Type a name or institute code"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <ComboboxList className={COMBOBOX_LIST_CLASS}>
          {(item: InstituteOption) => (
            <ComboboxItem key={item.value} value={item.value} className={DROPDOWN_ITEM_CLASS}>
              <span className="flex flex-col">
                <span>{item.label}</span>
                <span className="text-xs text-muted-foreground">
                  {[item.code, item.where].filter(Boolean).join(' · ')}
                </span>
              </span>
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxEmpty>
          {isFetching
            ? 'Searching…'
            : query.trim() === ''
              ? 'This ZM covers no institute yet. Give them one under Role & Organisation first.'
              : 'No institute of this ZM matches that.'}
        </ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}
